import { useState } from 'react';
import { Alert, Anchor, Button, Paper, Select, Stack, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import {
  useCompositionHistory,
  useCompositionScope,
} from '@/hooks/api/automations/useExecutionCompositions.ts';
import CompositionWorkspace from './CompositionWorkspace.tsx';
import { compositionOptions } from './compositionOptions.ts';

export default function CompositionBrowser({
  rootVersionId,
  initialReleaseId,
  mode = 'advanced',
  canEdit,
}: {
  rootVersionId: string;
  initialReleaseId?: string | null;
  mode?: 'advanced' | 'guided';
  canEdit: boolean;
}) {
  const scope = useCompositionScope();
  if (mode === 'guided')
    return (
      <Stack>
        <Text size="sm">
          Your draft and its execution triggers are included. Select exact child implementations;
          saving these selections creates an unpublished Release candidate for review.
        </Text>
        <Anchor
          component={Link}
          to={`?version=${encodeURIComponent(rootVersionId)}`}
          size="sm"
          w="fit-content"
        >
          Use advanced release settings instead
        </Anchor>
        <CompositionWorkspace rootVersionId={rootVersionId} mode="guided" canEdit={canEdit} />
      </Stack>
    );
  return (
    <BrowserSession
      key={JSON.stringify([...scope, rootVersionId, initialReleaseId])}
      rootVersionId={rootVersionId}
      initialReleaseId={initialReleaseId}
      canEdit={canEdit}
    />
  );
}
function BrowserSession({
  rootVersionId,
  initialReleaseId,
  canEdit,
}: {
  rootVersionId: string;
  initialReleaseId?: string | null;
  canEdit: boolean;
}) {
  const history = useCompositionHistory();
  const [session, setSession] = useState(0);
  const [releaseId, setReleaseId] = useState<string | null>(initialReleaseId ?? null);
  const [authoring, setAuthoring] = useState(Boolean(initialReleaseId));
  const openSavedRelease = (id: string | null) => {
    setReleaseId(id);
    setAuthoring(Boolean(id));
  };
  return (
    <Stack>
      {!authoring && (
        <>
          <Paper withBorder p="md">
            <Stack gap="xs">
              <Text fw={600}>Coordinate a release only when you need to</Text>
              <Text size="sm" c="dimmed">
                Choose exact dependency implementations, coordinate multiple automations, or add
                release-specific evidence. Saving a candidate never publishes it.
              </Text>
              <Button
                variant="filled"
                onClick={() => {
                  setReleaseId(null);
                  setSession((value) => value + 1);
                  setAuthoring(true);
                  void history.refetch();
                }}
              >
                Start coordinated release
              </Button>
            </Stack>
          </Paper>
          <Select
            label="Open saved advanced release"
            searchable
            disabled={!history.isFetchedAfterMount || history.isError}
            value={releaseId}
            onChange={openSavedRelease}
            data={compositionOptions(
              (history.isFetchedAfterMount && !history.isError
                ? history.data
                : undefined
              )?.pages.flatMap((page) => page.releases) ?? [],
              releaseId,
            )}
          />
          {history.hasNextPage && (
            <Button
              variant="subtle"
              disabled={!history.isFetchedAfterMount || history.isError || history.isFetching}
              onClick={() => void history.fetchNextPage()}
            >
              Load more compositions
            </Button>
          )}
          {history.isError && (
            <Stack gap="xs">
              <Text c="red">History unavailable. Refresh before selecting a candidate.</Text>
              <Button
                disabled={history.isFetching}
                onClick={() =>
                  void (history.isFetchNextPageError ? history.fetchNextPage() : history.refetch())
                }
              >
                Retry composition history
              </Button>
            </Stack>
          )}
        </>
      )}
      {authoring && (
        <>
          <Button
            variant="subtle"
            onClick={() => {
              setAuthoring(false);
              void history.refetch();
            }}
          >
            Back to advanced release candidates
          </Button>
          <CompositionWorkspace
            key={session}
            releaseId={releaseId ?? undefined}
            rootVersionId={releaseId ? undefined : rootVersionId}
            canEdit={canEdit}
            onSaved={(id) => {
              setReleaseId(id);
              setAuthoring(true);
              void history.refetch();
            }}
          />
          {history.isError && (
            <Alert color="red">
              Candidate history could not be refreshed. Your open candidate is unchanged.
              <Button
                variant="subtle"
                disabled={history.isFetching}
                onClick={() =>
                  void (history.isFetchNextPageError ? history.fetchNextPage() : history.refetch())
                }
              >
                Retry candidate history
              </Button>
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
}
